/**
 * FinanceAuditTab — chronological financial-action history per deal.
 *
 * Renders rows from `finance_audit_log` ordered newest-first. RLS gates
 * `is_financial=true` entries from RM/TL/ASM (per Ship 1's policy
 * `fal_read`) — the API call is the same; the row just doesn't return.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 11, §10c #6)
 */

import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fmtUSD } from "../../lib/analytics";
import { format, formatDistanceToNow } from "date-fns";

interface AuditRow {
  id: string;
  deal_id: string | null;
  actor_id: string | null;
  action: string;
  amount_delta: number | null;
  before_jsonb: unknown;
  after_jsonb: unknown;
  reason: string | null;
  is_financial: boolean;
  created_at: string;
}

// Pretty labels + colors per action kind. Defaults are slate-ish.
const ACTION_META: Record<string, { label: string; tint: string; icon: string }> = {
  commission_edit:                 { label: "Commission edited",            tint: "bg-orange-50 border-orange-200 text-orange-700", icon: "✎" },
  commission_repayment_applied:    { label: "Draw repayment applied",       tint: "bg-purple-50 border-purple-200 text-purple-700", icon: "💵" },
  commission_tier_promoted:        { label: "Tier promoted",                tint: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: "🎉" },
  roof_crew_payment_approved:      { label: "Roof crew approved",           tint: "bg-blue-50 border-blue-200 text-blue-700", icon: "🔨" },
  draw_approved:                   { label: "Draw approved",                tint: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: "✓" },
  draw_rejected:                   { label: "Draw rejected",                tint: "bg-red-50 border-red-200 text-red-700", icon: "✗" },
  draw_disbursed:                  { label: "Draw disbursed",               tint: "bg-purple-50 border-purple-200 text-purple-700", icon: "💸" },
  auto_promote_to_delinquent:      { label: "Auto-promoted delinquent",     tint: "bg-red-50 border-red-200 text-red-700", icon: "⚠" },
  qb_invoice_created:              { label: "QBO invoice created",          tint: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: "📄" },
  qb_invoice_voided:               { label: "QBO invoice voided",           tint: "bg-slate-100 border-slate-200 text-slate-700", icon: "✕" },
  qb_bill_created:                 { label: "QBO bill posted",              tint: "bg-blue-50 border-blue-200 text-blue-700", icon: "📋" },
  stripe_dispute_opened:           { label: "Stripe dispute opened",        tint: "bg-red-50 border-red-200 text-red-700", icon: "🆘" },
  stripe_refund_completed:         { label: "Stripe refund",                tint: "bg-slate-100 border-slate-200 text-slate-700", icon: "↩" },
};

function actionMeta(action: string) {
  return ACTION_META[action] ?? { label: action.replace(/_/g, " "), tint: "bg-slate-50 border-slate-200 text-slate-700", icon: "•" };
}

export default function FinanceAuditTab({ dealId }: { dealId: string }) {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [actors, setActors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("finance_audit_log", reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error: err } = await supabase
        .from("finance_audit_log")
        .select("*")
        .eq("deal_id", dealId)
        .order("created_at", { ascending: false })
        .limit(200);
      if (err) {
        if (!cancelled) setError(err.message);
        return;
      }
      if (cancelled) return;
      setRows((data ?? []) as AuditRow[]);

      const ids = Array.from(new Set((data ?? []).map((r: AuditRow) => r.actor_id).filter(Boolean))) as string[];
      if (ids.length > 0) {
        const { data: members } = await supabase.from("team_roster").select("id, full_name").in("id", ids);
        const map: Record<string, string> = {};
        (members ?? []).forEach((m: { id: string; full_name: string }) => { map[m.id] = m.full_name; });
        if (!cancelled) setActors(map);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [dealId, reloadKey]);

  if (error) return <div className="bg-red-50 text-red-700 p-3 rounded text-sm">{error}</div>;
  if (!rows) return <div className="p-4 text-slate-500 text-sm">Loading audit log…</div>;

  if (rows.length === 0) {
    return (
      <div className="p-4">
        <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
          <div className="text-2xl mb-2">📜</div>
          <div className="font-medium">No financial activity yet</div>
          <div className="text-xs mt-1 max-w-sm mx-auto">
            Every commission edit, payment state change, draw decision, write-off, and tier promotion writes here.
            RM/TL/ASM see only non-financial entries on this deal — financials are visible to admin / finance / sales-director.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="text-xs text-slate-500 mb-3">
        {rows.length} entr{rows.length === 1 ? "y" : "ies"} · newest first · auto-refreshes
      </div>

      <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3">
        {rows.map((r) => {
          const meta = actionMeta(r.action);
          const actor = r.actor_id ? actors[r.actor_id] ?? "—" : "system";
          const ts = new Date(r.created_at);
          return (
            <li key={r.id} className="ml-3 relative">
              <span className="absolute -left-[19px] top-1 w-3 h-3 rounded-full bg-white border-2 border-slate-300" />
              <div className={"rounded-lg border p-3 " + meta.tint}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-base">{meta.icon}</span>
                    <span className="text-sm font-semibold">{meta.label}</span>
                    {r.amount_delta != null && Number(r.amount_delta) !== 0 && (
                      <span className={"text-xs font-bold px-1.5 py-0.5 rounded " +
                        (Number(r.amount_delta) > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700")}>
                        {Number(r.amount_delta) > 0 ? "+" : ""}{fmtUSD(Number(r.amount_delta))}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500" title={format(ts, "PPpp")}>
                    {actor} · {formatDistanceToNow(ts, { addSuffix: true })}
                  </div>
                </div>
                {r.reason && (
                  <div className="text-xs text-slate-600 mt-2 italic">"{r.reason}"</div>
                )}
                {r.before_jsonb || r.after_jsonb ? (
                  <details className="mt-2">
                    <summary className="text-[11px] text-slate-500 cursor-pointer select-none hover:text-slate-700">
                      Show before / after
                    </summary>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Before</div>
                        <pre className="bg-slate-50 p-2 rounded text-slate-600 overflow-x-auto">{JSON.stringify(r.before_jsonb ?? null, null, 2)}</pre>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">After</div>
                        <pre className="bg-slate-50 p-2 rounded text-slate-600 overflow-x-auto">{JSON.stringify(r.after_jsonb ?? null, null, 2)}</pre>
                      </div>
                    </div>
                  </details>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
